import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import useSEO from "../../../hooks/useSEO";

const SchemaMarkup = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // SEO dinámico
  useSEO({
    title: "Schema Markup Avanzado - Datos Estructurados | SEOgenia",
    description: "Implementamos datos estructurados de última generación que convierten tu contenido en rich snippets irresistibles y alimentan a las IAs con información perfecta.",
    keywords: "schema markup, datos estructurados, rich snippets, schema.org, json-ld, seo técnico",
    canonical: "https://seogenia.es/servicios/schema-markup",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Schema Markup Avanzado",
      "description": "Implementación de datos estructurados para rich snippets y mejor comprensión por IAs",
      "provider": {
        "@type": "Organization",
        "name": "SEOgenia",
        "url": "https://seogenia.es"
      },
      "areaServed": "ES",
      "serviceType": "Schema Markup y Datos Estructurados"
    }
  });

  const faqs = [
    {
      question: "¿Qué es Schema Markup y por qué lo necesito?",
      answer: "Schema Markup es un código estructurado (JSON-LD) que añades a tu web para que Google y otros buscadores entiendan exactamente qué significa cada elemento: productos, precios, reseñas, eventos, recetas, etc. Lo necesitas porque los sitios con Schema Markup obtienen 30-40% más clics gracias a rich snippets (resultados enriquecidos con estrellas, precios, imágenes) que destacan visualmente en Google."
    },
    {
      question: "¿Cuánto tiempo tarda en aparecer en Google los rich snippets?",
      answer: "Una vez implementado el Schema Markup, Google necesita re-rastrear e indexar tus páginas. Esto puede tomar entre 1-4 semanas dependiendo de la frecuencia de rastreo de tu sitio. Los rich snippets no están garantizados (Google decide si mostrarlos), pero con Schema Markup correcto y contenido de calidad, la mayoría de nuestros clientes los obtienen en 2-6 semanas."
    },
    {
      question: "¿Todos los tipos de Schema funcionan en mi industria?",
      answer: "No todos los schemas son aplicables a todos los sitios. Analizamos tu contenido específico para identificar qué tipos de Schema Markup son relevantes y tienen mayor impacto. Por ejemplo: e-commerce usa Product Schema, blogs usan Article Schema, restaurantes usan LocalBusiness y Recipe Schema, eventos usan Event Schema, etc. Implementamos solo los schemas que realmente benefician tu negocio."
    },
    {
      question: "¿El Schema Markup mejora mi posicionamiento directamente?",
      answer: "Schema Markup no es un factor de ranking directo, pero mejora tu SEO indirectamente de forma muy significativa: aumenta tu CTR (más clics = mejor ranking), ayuda a Google a entender tu contenido (mejor indexación), aumenta probabilidades de featured snippets (posición #0), y mejora la experiencia de usuario (más confianza = menos rebote). El resultado neto es mejor posicionamiento."
    },
    {
      question: "¿Puedo implementar Schema Markup yo mismo?",
      answer: "Técnicamente sí, pero es complejo y propenso a errores. Schema Markup mal implementado puede ser ignorado por Google o incluso penalizar tu sitio. Requiere conocimientos de JSON-LD, comprensión de la documentación de Schema.org, validación con herramientas específicas, y mantenimiento continuo. Nuestra implementación profesional garantiza 0 errores y máximo impacto desde el primer día."
    },
    {
      question: "¿Qué diferencia hay entre JSON-LD, Microdata y RDFa?",
      answer: "Son tres formatos para implementar Schema Markup. JSON-LD (recomendado por Google) es código JavaScript separado del HTML, más fácil de mantener. Microdata se integra directamente en las etiquetas HTML. RDFa es similar a Microdata pero más complejo. Nosotros usamos principalmente JSON-LD porque es el formato preferido por Google, más limpio, y no interfiere con el diseño de tu web."
    },
    {
      question: "¿Los agentes de IA como ChatGPT usan Schema Markup?",
      answer: "Sí, absolutamente. ChatGPT, Gemini, Perplexity y otros agentes de IA utilizan Schema Markup para entender mejor tu contenido y extraer información estructurada. Cuando un agente de IA necesita datos específicos (precios, valoraciones, fechas, ubicaciones), Schema Markup facilita enormemente que encuentren y citen tu información correctamente."
    },
    {
      question: "¿Necesito actualizar el Schema Markup regularmente?",
      answer: "Sí, especialmente si tu contenido cambia frecuentemente. Precios de productos, disponibilidad de stock, fechas de eventos, nuevas reseñas, cambios en horarios de negocio, etc. deben reflejarse en tu Schema Markup. Ofrecemos planes de mantenimiento donde monitoreamos y actualizamos tu Schema Markup automáticamente para que siempre esté sincronizado con tu contenido real."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-slate-900"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full shadow-sm mb-6">
              <i className="ri-code-box-line text-purple-400"></i>
              <span className="text-sm font-medium text-white">Servicio Especializado</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Schema Markup Avanzado
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              Implementamos datos estructurados de última generación que convierten tu contenido en rich snippets irresistibles y alimentan a las IAs con información perfecta.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#contacto"
                className="px-8 py-3 bg-purple-600 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                Solicitar Implementación
              </a>
              <a
                href="#tipos"
                className="px-8 py-3 bg-white/10 text-white rounded-full font-medium hover:shadow-lg transition-all duration-300 border border-white/20 whitespace-nowrap"
              >
                Ver Tipos de Schema
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                ¿Qué es Schema Markup?
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Schema.org es el lenguaje universal que permite a buscadores e inteligencias artificiales entender exactamente qué significa cada elemento de tu contenido: productos, precios, reseñas, eventos, recetas, artículos, FAQs y mucho más.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                No es solo código técnico, es la diferencia entre aparecer como un resultado más o destacar con estrellas, precios, imágenes y datos que multiplican tu CTR hasta un 300%.
              </p>
              <div className="bg-gradient-to-br from-cyan-50 to-purple-50 p-6 rounded-xl border border-cyan-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-star-line text-yellow-500 mr-2"></i>
                  Impacto Real
                </h3>
                <p className="text-gray-700">
                  Los sitios con Schema Markup bien implementado obtienen un <strong>30-40% más de clics</strong> que sus competidores sin él, según estudios de Google.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/0a93af1805ddb748cd98b00f7ff47741.jpg"
                alt="Schema Markup Avanzado"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Schema Types Section */}
      <section id="tipos" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Tipos de Schema que Implementamos
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Más de 30 tipos diferentes de datos estructurados para cada necesidad
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "ri-shopping-bag-3-line",
                title: "Product Schema",
                description: "Precios, disponibilidad, reseñas y valoraciones visibles en resultados",
                features: ["Precio y moneda", "Stock disponible", "Estrellas de rating", "Marca y SKU"]
              },
              {
                icon: "ri-article-line",
                title: "Article Schema",
                description: "Artículos destacados con autor, fecha y imagen en Google News y Discover",
                features: ["Autor y fecha", "Imagen destacada", "Categoría", "Tiempo de lectura"]
              },
              {
                icon: "ri-calendar-event-line",
                title: "Event Schema",
                description: "Eventos con fecha, ubicación y precio que aparecen en búsquedas locales",
                features: ["Fecha y hora", "Ubicación", "Precio entrada", "Organizador"]
              },
              {
                icon: "ri-question-answer-line",
                title: "FAQ Schema",
                description: "Preguntas frecuentes expandibles directamente en resultados de búsqueda",
                features: ["Preguntas destacadas", "Respuestas visibles", "Más espacio SERP", "Autoridad"]
              },
              {
                icon: "ri-star-line",
                title: "Review Schema",
                description: "Valoraciones y opiniones con estrellas que generan confianza inmediata",
                features: ["Rating agregado", "Número reseñas", "Estrellas doradas", "Autor review"]
              },
              {
                icon: "ri-restaurant-line",
                title: "Recipe Schema",
                description: "Recetas con tiempo, ingredientes y valoraciones en formato visual",
                features: ["Tiempo cocción", "Calorías", "Ingredientes", "Pasos detallados"]
              },
              {
                icon: "ri-briefcase-line",
                title: "JobPosting Schema",
                description: "Ofertas de empleo destacadas en Google for Jobs con todos los detalles",
                features: ["Salario", "Ubicación", "Tipo contrato", "Fecha publicación"]
              },
              {
                icon: "ri-video-line",
                title: "Video Schema",
                description: "Vídeos con thumbnail, duración y descripción en resultados enriquecidos",
                features: ["Miniatura", "Duración", "Fecha subida", "Descripción"]
              },
              {
                icon: "ri-map-pin-line",
                title: "LocalBusiness Schema",
                description: "Información completa de negocio local para búsquedas geográficas",
                features: ["Dirección", "Horarios", "Teléfono", "Coordenadas GPS"]
              }
            ].map((schema, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${schema.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{schema.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{schema.description}</p>
                <ul className="space-y-2">
                  {schema.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ventajas Competitivas
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Por qué el Schema Markup es imprescindible en 2025
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: "ri-eye-line",
                title: "Mayor Visibilidad",
                stat: "+300%",
                description: "Incremento en CTR con rich snippets vs resultados normales"
              },
              {
                icon: "ri-robot-line",
                title: "IA-Ready",
                stat: "100%",
                description: "Compatible con ChatGPT, Gemini, Perplexity y futuros agentes"
              },
              {
                icon: "ri-trophy-line",
                title: "Posición #0",
                stat: "Featured",
                description: "Mayor probabilidad de aparecer en snippets destacados"
              },
              {
                icon: "ri-shield-check-line",
                title: "Validación",
                stat: "0 Errores",
                description: "Código validado con Google Rich Results Test"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <div className="text-4xl font-bold bg-gradient-to-r from-cyan-600 to-pink-600 bg-clip-text text-transparent mb-2">
                  {benefit.stat}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Proceso de Implementación
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Metodología profesional para garantizar resultados
            </p>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                step: "1",
                title: "Análisis de Contenido",
                description: "Identificamos todos los tipos de contenido en tu sitio y determinamos qué schemas son aplicables y prioritarios",
                icon: "ri-search-line"
              },
              {
                step: "2",
                title: "Diseño de Estrategia",
                description: "Creamos un plan detallado de implementación con prioridades basadas en impacto potencial y competencia",
                icon: "ri-lightbulb-line"
              },
              {
                step: "3",
                title: "Codificación JSON-LD",
                description: "Implementamos los schemas usando JSON-LD (formato recomendado por Google) con todos los campos relevantes",
                icon: "ri-code-s-slash-line"
              },
              {
                step: "4",
                title: "Testing y Validación",
                description: "Validamos cada schema con Google Rich Results Test y Schema Markup Validator para garantizar 0 errores",
                icon: "ri-checkbox-circle-line"
              },
              {
                step: "5",
                title: "Monitoreo de Resultados",
                description: "Seguimiento en Google Search Console de rich results, impresiones y CTR para medir el impacto real",
                icon: "ri-line-chart-line"
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-pink-500 rounded-2xl flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{process.step}</span>
                  </div>
                </div>
                <div className="flex-1 bg-white p-8 rounded-2xl shadow-lg">
                  <div className="flex items-center mb-3">
                    <i className={`${process.icon} text-2xl text-purple-600 mr-3`}></i>
                    <h3 className="text-2xl font-bold text-gray-900">{process.title}</h3>
                  </div>
                  <p className="text-lg text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Impacto en Resultados
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Empresas que multiplicaron su CTR con Schema Markup
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: "Fernando López",
                company: "Recetas Gourmet",
                role: "Fundador",
                text: "Los rich snippets con estrellas y tiempo de cocción aumentaron nuestro CTR un 320%. Ahora destacamos visualmente sobre toda la competencia.",
                rating: 5
              },
              {
                name: "Cristina Vega",
                company: "Eventos Premium",
                role: "Directora Marketing",
                text: "El Event Schema nos puso en el mapa de Google. Las reservas directas desde búsquedas orgánicas se triplicaron en 2 meses.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-800 p-8 rounded-2xl border border-gray-700"
              >
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-lg text-white leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-bold text-white text-lg">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-300">
                    {testimonial.role} en {testimonial.company}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-lg text-gray-600">
              Resolvemos tus dudas sobre Schema Markup
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-8">
                    {faq.question}
                  </h3>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-purple-600 flex-shrink-0 transition-transform duration-300 ${
                      openFAQ === index ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFAQ === index ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Destaca en Resultados con Rich Snippets
            </h2>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Implementa Schema Markup profesional y multiplica tu visibilidad
            </p>
            <Link
              to="/#contacto"
              className="inline-block px-8 py-4 bg-white text-gray-900 rounded-full font-bold hover:shadow-2xl transition-all duration-300 whitespace-nowrap"
            >
              Solicitar Implementación Ahora
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para implementar Schema Markup avanzado?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Descubre cómo los datos estructurados pueden transformar tu visibilidad
          </p>
          <Link 
            to="/#contacto"
            className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-full font-medium text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            Solicitar Consulta Gratuita
          </Link>
        </div>
      </section>

      {/* Servicios Relacionados */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Servicios Complementarios
            </h2>
            <p className="text-xl text-gray-600">
              Potencia tu SEO con estos servicios relacionados
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Arquitectura Semántica */}
            <Link 
              to="/servicios/arquitectura-semantica"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-layout-grid-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Arquitectura Semántica
              </h3>
              <p className="text-gray-600 mb-4">
                Construye la base perfecta para tu Schema Markup con una arquitectura HTML5 semántica profesional.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* SEO Agentes IA */}
            <Link 
              to="/servicios/seo-agentes-ia"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-robot-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                SEO para Agentes IA
              </h3>
              <p className="text-gray-600 mb-4">
                Combina Schema Markup con optimización para agentes IA y maximiza tu visibilidad en búsquedas conversacionales.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>

            {/* Voice Search SEO */}
            <Link 
              to="/servicios/voice-search-seo"
              className="group bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-cyan-400"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-mic-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-400 transition-colors">
                Voice Search SEO
              </h3>
              <p className="text-gray-600 mb-4">
                Potencia tu Schema Markup para búsquedas por voz y asistentes virtuales como Alexa, Siri y Google Assistant.
              </p>
              <div className="flex items-center text-cyan-400 font-medium group-hover:gap-3 transition-all">
                <span>Descubrir más</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SchemaMarkup;